import { boundMethod } from 'autobind-decorator';
import { AsyncStatus, TeamAgent } from 'lib-shared-legacy-types';
import isEqual from 'lodash/isEqual';
import memoize from 'memoize-one';
import { ComponentType, PureComponent } from 'react';

import { ajaxSend } from 'api/api';

import {
  addAgentsToTeam,
  getTeamAgents,
  removeAgentsFromTeam,
} from './api/teamAgents';
import { TeamAgentsGetItemsResponse } from './api/types';

const DEFAULT_FETCH_THROTTLE = 300;

export interface WithTeamAgentsInjectedProps {
  teamAgents: {
    /** Loaded items of current page */
    items: Array<TeamAgent>;

    /** AsyncStatus of get items api */
    getItemsAsyncStatus: AsyncStatus;

    /** AsyncStatus of add agents in teams. */
    addAsyncStatus: AsyncStatus;

    /** AsyncStatus of remove agents from team api */
    removeAsyncStatus: AsyncStatus;

    /** The search term used in get items api */
    searchTerm: string;

    /** The sorting option used in get items api */
    sortBy: string;

    /** Whether there is next page or not */
    hasNextPage: boolean;

    /** Whether there is previous page or not */
    hasPreviousPage: boolean;

    /**
     * Action to get items. It will use the current search term and sorting to
     * filter items.
     */
    getItems: () => Promise<TeamAgentsGetItemsResponse>;

    /**
     * Handle search term change.
     *
     * @param {string} searchTerm - The search term
     * @param {number} [fetchThrottle=300] - If > 0 it will re-fetch items after
     *   given milliseconds. Default is `300`
     */
    searchTermChange: (
      searchTerm?: string,
      fetchThrottle?: number,
    ) => Promise<TeamAgentsGetItemsResponse | void>;

    /**
     * Handle sorting change.
     *
     * @param {string} sortBy - Column to sort the list by
     * @param {string} direction - "asc" or "desc", if missing the default is
     *   "asc" on the backend
     * @param {boolean} [refetch=true] - Whether to refetch list after changing
     *   sort. Defaults to true. Default is `true`
     */
    sortByChange: (
      sortBy?: string,
      direction?: string,
      refetch?: boolean,
    ) => Promise<TeamAgentsGetItemsResponse | void>;

    /** Get agents of the next page */
    nextPage: () => Promise<TeamAgentsGetItemsResponse>;

    /** Get agents of the previous page */
    previousPage: () => Promise<TeamAgentsGetItemsResponse>;

    /**
     * Add new agents to team
     *
     * @param {string[]} agentIds - IDs of agents to be added.
     */
    add: (agentIds: string[]) => Promise<unknown>;

    /**
     * Remove agents
     *
     * @param {string[]} agentIds - Ids of the agents to be removed.
     */
    remove: (agentIds: string[]) => Promise<unknown>;
  };
}

type WithTeamAgentsNotInjectedProps<T> = Omit<
  T,
  keyof WithTeamAgentsInjectedProps
>;

type WithTeamAgentsProps<T> = WithTeamAgentsNotInjectedProps<T> & {
  teamId: string;
};

interface WithTeamAgentsState {
  searchTerm: string;
  sortBy: string;
  items: Array<TeamAgent>;
  nextPagePath: string | null;
  previousPagePath: string | null;
  getItemsAsyncStatus: AsyncStatus;
  addAsyncStatus: AsyncStatus;
  removeAsyncStatus: AsyncStatus;
}

interface WithTeamAgentsSettings {
  pageSize?: number;
}

export const withTeamAgents = <T extends WithTeamAgentsInjectedProps>(
  Component: ComponentType<T>,
  settings: WithTeamAgentsSettings = {
    pageSize: 25,
  },
) => {
  class WithTeamAgents extends PureComponent<
    WithTeamAgentsProps<T>,
    WithTeamAgentsState
  > {
    timeout: ReturnType<typeof setTimeout> | null;

    constructor(props: WithTeamAgentsProps<T>) {
      super(props);

      this.timeout = null;
      this.state = {
        items: [],
        searchTerm: '',
        sortBy: 'display_name:asc',
        nextPagePath: null,
        previousPagePath: null,
        getItemsAsyncStatus: 'succeeded',
        addAsyncStatus: 'succeeded',
        removeAsyncStatus: 'succeeded',
      };
      this.memoizedGetInjectedProps = memoize(
        this.memoizedGetInjectedProps,
        isEqual,
      ).bind(this); // We have to bind it like this. Using @boundMethod won't work
    }

    componentWillUnmount() {
      if (this.timeout) {
        clearTimeout(this.timeout);
      }
    }

    @boundMethod
    getItems(path?: string): Promise<TeamAgentsGetItemsResponse> {
      this.setState({ getItemsAsyncStatus: 'loading' });

      const req = path
        ? ajaxSend<TeamAgentsGetItemsResponse>({ path })
        : getTeamAgents(this.props.teamId, {
            search: this.state.searchTerm,
            limit: settings.pageSize,
            sort_by: !this.state.searchTerm ? this.state.sortBy : '', // We don't support sort and search at the same time
          });

      return req
        .then((response) => {
          this.setState({
            getItemsAsyncStatus: 'succeeded',
            items: response.data.agents,
            nextPagePath: response.meta.next_page,
            previousPagePath: response.meta.prev_page,
          });

          return response;
        })
        .catch((error) => {
          this.setState({
            getItemsAsyncStatus: 'failed',
          });

          return error;
        });
    }

    @boundMethod
    nextPage(): Promise<TeamAgentsGetItemsResponse> | never {
      const { nextPagePath } = this.state;
      if (nextPagePath) {
        return this.getItems(nextPagePath);
      }
      throw new Error('Next page does not exist');
    }

    @boundMethod
    previousPage(): Promise<TeamAgentsGetItemsResponse> | never {
      const { previousPagePath } = this.state;
      if (previousPagePath) {
        return this.getItems(previousPagePath);
      }
      throw new Error('Previous page does not exist');
    }

    @boundMethod
    add(agentIds): Promise<unknown> {
      this.setState({ addAsyncStatus: 'loading' });
      return addAgentsToTeam(this.props.teamId, agentIds)
        .then((response) => {
          this.setState({ addAsyncStatus: 'succeeded' });
          return response;
        })
        .catch((err) => {
          this.setState({ addAsyncStatus: 'failed' });
          throw err;
        });
    }

    @boundMethod
    remove(agentIds: string[]): Promise<unknown> {
      this.setState({ removeAsyncStatus: 'loading' });
      return removeAgentsFromTeam(this.props.teamId, agentIds)
        .then((response) => {
          this.setState({ removeAsyncStatus: 'succeeded' });
          return response;
        })
        .catch((err) => {
          this.setState({ removeAsyncStatus: 'failed' });
          throw err;
        });
    }

    @boundMethod
    handleSearchTermChange(
      searchTerm = '',
      fetchThrottle: number = DEFAULT_FETCH_THROTTLE,
    ) {
      return new Promise<TeamAgentsGetItemsResponse | void>((resolve) => {
        this.setState({ searchTerm }, () => {
          if (fetchThrottle >= 0) {
            if (this.timeout) {
              clearTimeout(this.timeout);
            }

            this.timeout = setTimeout(() => {
              resolve(this.getItems());
            }, fetchThrottle);
          } else {
            resolve();
          }
        });
      });
    }

    @boundMethod
    handleSortByChange(sortBy, direction = 'asc', refetch = true) {
      return new Promise<TeamAgentsGetItemsResponse | void>((resolve) => {
        this.setState(
          {
            sortBy: `${sortBy}:${direction}`,
          },
          () => {
            if (refetch) {
              resolve(this.getItems());
            } else {
              resolve();
            }
          },
        );
      });
    }

    @boundMethod
    getInjectedProps(
      props: Readonly<WithTeamAgentsProps<T>>,
      state: Readonly<WithTeamAgentsState>,
    ): WithTeamAgentsInjectedProps['teamAgents'] {
      const {
        items,
        searchTerm,
        sortBy,
        nextPagePath,
        previousPagePath,
        getItemsAsyncStatus,
        addAsyncStatus,
        removeAsyncStatus,
      } = state;

      return this.memoizedGetInjectedProps(
        searchTerm,
        sortBy,
        items,
        getItemsAsyncStatus,
        addAsyncStatus,
        removeAsyncStatus,
        nextPagePath,
        previousPagePath,
      );
    }

    memoizedGetInjectedProps(
      searchTerm: Readonly<WithTeamAgentsState>['searchTerm'],
      sortBy: Readonly<WithTeamAgentsState>['sortBy'],
      items: Readonly<WithTeamAgentsState>['items'],
      getItemsAsyncStatus: Readonly<WithTeamAgentsState>['getItemsAsyncStatus'],
      addAsyncStatus: Readonly<WithTeamAgentsState>['addAsyncStatus'],
      removeAsyncStatus: Readonly<WithTeamAgentsState>['removeAsyncStatus'],
      nextPagePath: Readonly<WithTeamAgentsState>['nextPagePath'],
      previousPagePath: Readonly<WithTeamAgentsState>['previousPagePath'],
    ): WithTeamAgentsInjectedProps['teamAgents'] {
      return {
        searchTerm: searchTerm,
        sortBy: sortBy,
        items: items,
        getItemsAsyncStatus: getItemsAsyncStatus,
        addAsyncStatus: addAsyncStatus,
        removeAsyncStatus: removeAsyncStatus,
        hasNextPage: nextPagePath !== null,
        hasPreviousPage: previousPagePath !== null,
        getItems: this.getItems,
        searchTermChange: this.handleSearchTermChange,
        sortByChange: this.handleSortByChange,
        nextPage: this.nextPage,
        previousPage: this.previousPage,
        add: this.add,
        remove: this.remove,
      };
    }

    render() {
      return (
        <Component
          {...(this.props as unknown as T)} // There is no easy way to remove `as unknown` in HOC accepting own props
          teamAgents={this.getInjectedProps(this.props, this.state)}
        />
      );
    }
  }

  return WithTeamAgents;
};
